import React, { useState } from "react";
import { Flex, Text, Box, type FlexProps } from "@chakra-ui/react";
import { type EventsPage } from "lib/sanity";
import { identifyUser, trackEvent, trackMutinyConversion } from "lib/analytics";
import { format, parseISO } from "date-fns";
import ReactMarkdown from "react-markdown";
import { Link } from "ui/link";
import { CalendarIcon, ClockIcon, PinIcon } from "ui/icons";
import { formatDateWithYear } from "utils";
import { Balancer } from "react-wrap-balancer";
import { Button } from "ui/button";
import { TextTitle } from "ui/text";
import { Section } from "../section";
import { HubSpotForm } from "../hubspot";

const defaultPrivacyPolicy = (
  <>
    I agree that by signing up for this event, I am expressly consenting to provide my personal data to Hightouch and
    the co-sponsor(s) of the event. Hightouch and each co-sponsor may use my personal data in accordance with their
    privacy policy (see our{" "}
    <Link variant="inlineDark" target="_blank" href="/privacy-policy">
      Privacy Policy
    </Link>
    ).
  </>
);

function DetailsContainer({ children }: FlexProps) {
  return (
    <Flex align="center" gap={3}>
      {children}
    </Flex>
  );
}

type EventHeroProps = Pick<
  EventsPage,
  | "type"
  | "title"
  | "description"
  | "startDate"
  | "endDate"
  | "time"
  | "location"
  | "hubSpotFormId"
  | "hubSpotFormHeader"
  | "hubSpotFormAdditionalFields"
  | "customPrivacyPolicy"
  | "sfdcCampaignId"
  | "pastEventCtaText"
  | "pastEventCtaLink"
>;

export function EventHero({
  type,
  title,
  description,
  startDate,
  endDate,
  time,
  location,
  hubSpotFormId,
  hubSpotFormHeader,
  hubSpotFormAdditionalFields,
  customPrivacyPolicy,
  sfdcCampaignId,
  pastEventCtaText,
  pastEventCtaLink,
}: EventHeroProps) {
  const [success, setSuccess] = useState(false);
  const now = new Date();
  const today = parseISO(format(now, "yyyy-MM-dd")); // Ensure we don't compare time stamps and the current date is not considered "past".
  const isPastEvent = endDate ? parseISO(endDate) < today : parseISO(startDate) < today;

  const onFormSubmit = (lead: Record<string, string>) => {
    trackEvent("Event RSVP Submitted", {
      Email: lead.email,
      "First Name": lead.firstname,
      "Last Name": lead.lastname,
      Company: lead.company,
      "Job Title": lead.jobtitle,
      page: window.location.href,
    });
    identifyUser({
      email: lead.email,
      firstName: lead.firstname,
      lastName: lead.lastName,
      companyName: lead.company,
    });
    trackMutinyConversion("event-rsvp");
    setSuccess(true);
  };

  return (
    <Section my={{ base: "bigger", md: "biggest" }}>
      <Flex
        direction={{ base: "column", lg: "row" }}
        gap={{ base: "bigger", md: "biggest" }}
        justifyContent="center"
        alignItems={{ base: "stretch", sm: "center" }}
      >
        <TextTitle
          flex={1}
          size="lg"
          chip={type ? { text: type } : undefined}
          title={<Balancer>{title}</Balancer>}
          descriptionAs="div" // Need this to prevent hydration errors with nested MDX from Sanity
          description={
            description && (
              <Box color="brand.text.light">
                <Box textStyle="article.p">
                  <ReactMarkdown
                    components={{
                      a: ({ node, href, ...props }) => <Link href={href} variant="inlineDark" {...props} />,
                      p: ({ node, ...props }) => <Text as="p" fontSize="inherit" mb={4} {...props} />,
                    }}
                  >
                    {description}
                  </ReactMarkdown>
                </Box>

                <Flex flexDir="column" gap="smaller" textStyle="article.footer">
                  <DetailsContainer>
                    <CalendarIcon />
                    <Text as="span">
                      {formatDateWithYear(startDate)}
                      {endDate && endDate !== startDate && ` - ${formatDateWithYear(endDate)}`}
                    </Text>
                  </DetailsContainer>

                  <DetailsContainer>
                    <ClockIcon />
                    <Text as="span">{time}</Text>
                  </DetailsContainer>

                  <DetailsContainer>
                    <PinIcon />
                    <Text as="span">{location}</Text>
                  </DetailsContainer>
                </Flex>
              </Box>
            )
          }
        />

        <Flex
          flex={1}
          maxW="md"
          flexDir="column"
          bg="brand.backgroundSecondary.light"
          borderRadius="base"
          alignSelf="stretch"
          justifyContent="center"
          mx={{ base: "auto", lg: 0 }}
        >
          {isPastEvent ? (
            <TextTitle
              size="sm"
              align="center"
              mobileAlign="center"
              title="RSVP Closed"
              description="The registration deadline has passed."
              actions={
                <>
                  {pastEventCtaText && pastEventCtaLink && (
                    <Button variant="secondary" href={pastEventCtaLink} mx="auto">
                      {pastEventCtaText}
                    </Button>
                  )}

                  <Button hasRightChevron href="/events" mx="auto">
                    Explore our other events
                  </Button>
                </>
              }
              px="bigger"
              py="biggest"
            />
          ) : (
            <Box m={{ base: "big", md: "bigger" }} maxW="sm">
              {!success && hubSpotFormHeader && <TextTitle size="xs" title={hubSpotFormHeader} mb="small" />}

              <HubSpotForm
                enableAutoComplete={false}
                displayedFields={["firstname", "lastname", "company", "jobtitle"]}
                additionalCustomFields={hubSpotFormAdditionalFields}
                label="Submit"
                formId={hubSpotFormId}
                successContent={
                  success && (
                    <Text align="center" textStyle="headline2">
                      RSVP submitted!
                    </Text>
                  )
                }
                sfdcCampaignId={sfdcCampaignId}
                legalText={
                  customPrivacyPolicy ? (
                    <ReactMarkdown
                      components={{
                        p: ({ node, ...props }) => <Text textStyle="caption2" {...props} />,
                        a: ({ node, href, ...props }) => (
                          <Link href={href} variant="inlineDark" target="_blank" {...props} />
                        ),
                      }}
                    >
                      {customPrivacyPolicy}
                    </ReactMarkdown>
                  ) : (
                    <Text textStyle="caption2">{defaultPrivacyPolicy}</Text>
                  )
                }
                onSubmit={onFormSubmit}
              />
            </Box>
          )}
        </Flex>
      </Flex>
    </Section>
  );
}
